import cx from "classnames";
import { Globe } from "lucide-react";
import { useState } from "react";

import { Icon } from "atoms/icon/Icon";
import Check from "atoms/icon/icons/ic_check_small_ol.svg";
import ChevronRight from "atoms/icon/icons/ic_chevron_right_small_ol.svg";
import { Modal } from "atoms/modal/Modal";
import { Button } from "atoms/button/Button";
import { Analytics } from "modules/analytics";
import { storage } from "modules/storage";
import { useUser } from "modules/user";

import { useI18n } from "../useI18n/useI18n";
import { useTranslation } from "../useTranslation/useTranslation";
import type { Dictionary } from "../__generated__/dictionary";
import type { BCP47LangCode } from "../i18n.types";

const LANGUAGE_LABEL_KEYS: Record<BCP47LangCode, keyof Dictionary> = {
  "en-US": "languageSwitcher.language.en-US",
  "en-CA": "languageSwitcher.language.en-CA",
  "en-GB": "languageSwitcher.language.en-GB",
  "es-MX": "languageSwitcher.language.es-MX",
  "fr-CA": "languageSwitcher.language.fr-CA",
  "fr-FR": "languageSwitcher.language.fr-FR",
  "de-DE": "languageSwitcher.language.de-DE",
  "it-IT": "languageSwitcher.language.it-IT",
  "ja-JP": "languageSwitcher.language.ja-JP",
  "ko-KR": "languageSwitcher.language.ko-KR",
  "vi-VN": "languageSwitcher.language.vi-VN",
  "tl-PH": "languageSwitcher.language.tl-PH",
  "zh-Hans": "languageSwitcher.language.zh-Hans",
  "zh-Hant": "languageSwitcher.language.zh-Hant",
};

const acknowledgedDisclaimerStorageKey = (lang: BCP47LangCode) =>
  `languageSwitcher.translationDisclaimerAcknowledged.${lang}`;

const hasAcknowledgedTranslationDisclaimer = (lang: BCP47LangCode): boolean =>
  storage.getItem(acknowledgedDisclaimerStorageKey(lang)) === "true";

const acknowledgeTranslationDisclaimer = (lang: BCP47LangCode): void => {
  storage.setItem(acknowledgedDisclaimerStorageKey(lang), "true");
};

/**
 * A settings-row language picker for the patient app. Displays the currently
 * active language and opens a modal to switch between supported languages.
 * The chosen language is persisted to localStorage / cookie by i18next.
 */
export const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const { currentLang, languages, changeLanguage } = useI18n();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingLang, setPendingLang] = useState<BCP47LangCode | null>(null);

  const applyLanguageChange = (lang: BCP47LangCode) => {
    void changeLanguage(lang);
    // Sync the chosen language to the patient's Customer.io/Segment profile so
    // messaging can target by language (identify merges this single trait).
    if (user?.id) {
      Analytics.identify(user.id, { language: lang });
    }
    setIsOpen(false);
  };

  const handleSelect = async (lang: BCP47LangCode) => {
    if (lang === "en-US" || hasAcknowledgedTranslationDisclaimer(lang)) {
      applyLanguageChange(lang);
      return;
    }
    // Non-English translations in this app are machine-generated and may
    // contain mistakes. Require a one-time, per-language acknowledgment
    // before switching, so patients aren't silently shown unverified text.
    // Preload the target language's dictionary first so the disclaimer's
    // translated copy is available immediately instead of flashing missing
    // text while i18next lazy-loads the resource bundle.
    await i18n.loadLanguages(lang);
    setPendingLang(lang);
  };

  const handleConfirmDisclaimer = () => {
    if (!pendingLang) return;
    acknowledgeTranslationDisclaimer(pendingLang);
    applyLanguageChange(pendingLang);
    setPendingLang(null);
  };

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-4 bg-white p-4 text-left text-body1 text-primary"
        onClick={() => setIsOpen(true)}
      >
        <Globe className="h-6 w-6 text-secondary" />
        <div className="flex-1">{t("languageSwitcher.rowLabel")}</div>
        <div className="text-body3 text-secondary">{t(LANGUAGE_LABEL_KEYS[currentLang])}</div>
        <Icon svg={ChevronRight} color="brandColor" size="xsmall" />
      </button>

      <Modal open={isOpen} onClose={() => setIsOpen(false)} size="h-auto">
        <div className="px-4 pb-6 pt-6">
          <h2 className="mb-6 text-center font-serif text-h2 text-primary">
            {t("languageSwitcher.title")}
          </h2>
          <div className="flex flex-col gap-2">
            {languages.map((lang) => {
              const isActive = lang === currentLang;
              return (
                <button
                  key={lang}
                  type="button"
                  className={cx(
                    "flex w-full items-center justify-between rounded-xl border p-4 text-left transition-colors active:bg-gray-50",
                    isActive ? "border-primary" : "border-gray-200",
                  )}
                  onClick={() => handleSelect(lang)}
                >
                  <span className="text-body1 text-primary">{t(LANGUAGE_LABEL_KEYS[lang])}</span>
                  {isActive && <Icon svg={Check} color="brandColor" size="small" />}
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

      {pendingLang && (
        <Modal open onClose={() => setPendingLang(null)} size="h-auto">
          <div className="px-4 pb-6 pt-6">
            <h2 className="mb-4 text-center font-serif text-h2 text-primary">
              {t("languageSwitcher.translationDisclaimerTitle")}
            </h2>
            <p className="mb-2 text-center text-body2 text-primary">
              {t("languageSwitcher.translationDisclaimerMessage", { lng: "en-US" })}
            </p>
            <p className="mb-6 text-center text-body2 text-secondary">
              {t("languageSwitcher.translationDisclaimerMessage", { lng: pendingLang })}
            </p>
            <Button fullWidth size="large" onClick={handleConfirmDisclaimer}>
              {t("languageSwitcher.translationDisclaimerConfirm", { lng: pendingLang })}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
